import { CreateLogger, filterClasses } from "@swivl-mono/helpers";
import { Channel, Log } from "@swivl-mono/types";
import { AnimatePresence, animate, motion, useMotionValue } from "motion/react";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { GetChannelFromParam } from "../../Helpers/Conversations/Conversations.Helper";
import CallModel, { CallBarPosition, CallStatus } from "../../Models/CallModel/Call.Model";
import { UsersModel } from "../../Models/Users/Users.Model";
import { VoiceCallBarCallEndedView } from "./States/VoiceCallBar.CallEnded.View";
import { VoiceCallBarInProgressView } from "./States/VoiceCallBar.InProgress.View";
import { VoiceCallBarRingingView } from "./States/VoiceCallBar.Ringing.View";

const log = CreateLogger(Log.VOICE, "VoiceCallBar");

enum VoiceCallBarState {
  RINGING = "RINGING",
  IN_PROGRESS = "IN_PROGRESS",
  CALL_ENDED = "CALL_ENDED"
}

export function VoiceCallBar() {
  const { escalationId, callBarPosition, isMuted, escalationUserId, callStatus, callerNumber, callStartedAt } = CallModel.useState();
  const { escalations } = UsersModel.useState();
  console.log("VCB - escalations :", escalations, escalationId);
  const escalation = escalationId ? escalations?.[escalationId] : undefined;
  const [offsetX, setOffsetX] = useState(16);
  const [callEndedAt, setCallEndedAt] = useState<Date | null>(null);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const right = useMotionValue(offsetX);

  const [elapsedTime, setElapsedTimed] = useState("");

  // calculate offsetX on position change and on window resize
  useEffect(() => {
    const calculateOffset = () => {
      if (callBarPosition === CallBarPosition.BottomCenter) {
        const columnWidth = window.innerWidth / 3;
        const barWidth = columnWidth - 32; // 32px for padding
        const centerX = (window.innerWidth - barWidth) / 2;
        setOffsetX(centerX);
      } else {
        setOffsetX(16); // bottom-right position
      }
    };

    calculateOffset();
    window.addEventListener("resize", calculateOffset);
    return () => window.removeEventListener("resize", calculateOffset);
  }, [callBarPosition]);

  // start with correct position if in a call page
  useEffect(() => {
    const channelSearchParam = searchParams.get("channel");
    if (
      location.pathname.startsWith("/contact-center/conversations/user/" + escalationUserId) &&
      channelSearchParam &&
      GetChannelFromParam(channelSearchParam) === Channel.VOICE
    ) {
      CallModel.setState({ callBarPosition: CallBarPosition.BottomCenter });
    }
  }, [searchParams]);

  useEffect(() => {
    if (!escalation || callBarPosition === CallBarPosition.Hidden) {
      return;
    }
    const channelSearchParam = searchParams.get("channel");
    if (
      location.pathname.startsWith("/contact-center/conversations/user/" + escalationUserId) &&
      channelSearchParam &&
      GetChannelFromParam(channelSearchParam) === Channel.VOICE
    ) {
      CallModel.setState({ callBarPosition: CallBarPosition.BottomCenter });
    } else {
      CallModel.setState({ callBarPosition: CallBarPosition.BottomRight });
    }
    // not in a call page, show the call bar if needed
    log("[VoiceCallBar] Not in a call page, showing call bar if needed", location.pathname);
  }, [location, callBarPosition, escalationUserId, searchParams]);

  useEffect(() => {
    if (!callStartedAt) {
      return;
    }

    const intervalId = setInterval(() => {
      const elapsed = Math.floor((Date.now() - callStartedAt.getTime()) / 1000);
      const minutes = Math.floor(elapsed / 60);
      const seconds = elapsed % 60;
      setElapsedTimed(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    }, 1000); // Update every 1 second

    // Cleanup function to clear the interval
    return () => clearInterval(intervalId);
  }, [callStartedAt, callStatus]); // Re-run effect if secondsLeft changes (e.g., if reset)

  let state = VoiceCallBarState.RINGING;
  if (callStatus === CallStatus.RINGING) {
    state = VoiceCallBarState.RINGING;
  } else if (callStatus === CallStatus.OPEN) {
    state = VoiceCallBarState.IN_PROGRESS;
  } else {
    state = VoiceCallBarState.CALL_ENDED;
  }

  useEffect(() => {
    if (state === VoiceCallBarState.CALL_ENDED) {
      setCallEndedAt(new Date());
    } else {
      setCallEndedAt(null);
    }
    // do a timer to hide the call ended state after 5 seconds
    if (state === VoiceCallBarState.CALL_ENDED) {
      const timer = setTimeout(() => {
        CallModel.setState({ callBarPosition: CallBarPosition.Hidden });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [state]);

  // animate call bar position x value when changing channels
  useEffect(() => {
    if (callBarPosition === CallBarPosition.Hidden) {
      return;
    }

    animate(right, offsetX, {
      duration: 0.3,
      ease: "easeInOut"
    });
  }, [offsetX]);

  const shouldShow = (escalation && !escalation.resolved) || callStatus === CallStatus.OPEN;

  return (
    <AnimatePresence mode="wait">
      {shouldShow && (
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className={filterClasses(
            "fixed bottom-4 z-300 flex h-[74px] w-[calc(33.33%-2rem)] items-center justify-between rounded-lg bg-black/[0.82] p-4 text-white",
            "shadow-[0_6px_16px_rgba(0,0,0,0.25)] backdrop-blur-[7.5px]"
          )}
          initial={{ opacity: 0, y: 80 }}
          exit={{ opacity: 0, y: 80 }}
          transition={{
            opacity: { duration: 0.2, ease: "easeInOut" },
            y: { duration: 0.2, ease: "easeOut" }
          }}
          style={{ right }}
        >
          {escalation && state === VoiceCallBarState.RINGING && <VoiceCallBarRingingView callerNumber={callerNumber} />}
          {escalation && state === VoiceCallBarState.IN_PROGRESS && (
            <VoiceCallBarInProgressView
              callStatus={callStatus}
              escalation={escalation}
              callerNumber={callerNumber}
              elapsedTime={elapsedTime}
              isMuted={isMuted}
            />
          )}
          {state === VoiceCallBarState.CALL_ENDED && <VoiceCallBarCallEndedView callerNumber={callerNumber} />}
          {/* {state === VoiceCallBarState.IN_PROGRESS && <VoiceCallBarInProgressView callerNumber={callerNumber} elapsedTime={elapsedTime} isMuted={isMuted} />} */}
          {/* {state === VoiceCallBarState.CALL_ENDED && <VoiceCallBarCallEndedView callerNumber={callerNumber} elapsedTime={elapsedTime} isMuted={isMuted} />} */}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
